import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import supabase from "../supabaseClient";
import "./Home.css";

export default function Home() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    yearsInBusiness: 15,
    totalProjects: 3,
    totalEmployees: 50,
    companiesCount: 6,
  });

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch companies with achievements
        const { data: companiesData, error: companiesError } = await supabase
          .from("companies")
          .select(
            "id, name, logo_url, short_description, founded_year, employees_count"
          );

        if (!isMounted) return;

        if (companiesError) {
          console.error("Companies fetch error:", companiesError);
          return;
        }

        // Fetch projects for metrics
        const { data: projectsData, error: projectsError } = await supabase
          .from("projects")
          .select("id, status")
          .in("status", ["completed", "ongoing"]);
        if (projectsError) {
          console.error("Projects fetch error:", projectsError);
        }
        console.log("Projects data:", projectsData);
        if (companiesData) {
          setCompanies(companiesData);

          // Calculate metrics
          const totalEmployees = companiesData.reduce(
            (sum, company) => sum + (company.employees_count || 0),
            0
          );

          setMetrics({
            yearsInBusiness: 15,
            totalProjects: projectsData?.length || 0,
            totalEmployees,
            companiesCount: companiesData.length,
          });
        }
      } catch (err) {
        if (isMounted) {
          console.error("Unexpected error:", err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="loading-text">Loading our companies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero">
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <div className="row align-items-center min-vh-100">
            <div className="col-lg-8 mx-auto text-center">
              <div className="hero-badge">
                <span className="badge-text">In Service for Past 15 Years</span>
              </div>
              <h1 className="hero-title mb-4">Vachana Group</h1>
              <p className="hero-subtitle mb-5">
                Building lasting partnerships through established expertise,
                unwavering trust, and proven results across diverse industries.
              </p>

              {/* Key Metrics */}
              <div className="hero-metrics row justify-content-center mb-5">
                <div className="col-md-3 col-6">
                  <div className="metric-card">
                    <div className="metric-number">
                      {metrics.yearsInBusiness}+
                    </div>
                    <div className="metric-label">Years of Excellence</div>
                  </div>
                </div>
                <div className="col-md-3 col-6">
                  <div className="metric-card">
                    <div className="metric-number">
                      {metrics.companiesCount}
                    </div>
                    <div className="metric-label">Established Companies</div>
                  </div>
                </div>
                <div className="col-md-3 col-6">
                  <div className="metric-card">
                    <div className="metric-number">
                      {metrics.totalProjects}+
                    </div>
                    <div className="metric-label">Projects</div>
                  </div>
                </div>
                <div className="col-md-3 col-6">
                  <div className="metric-card">
                    <div className="metric-number">
                      {metrics.totalEmployees}+
                    </div>
                    <div className="metric-label">Team Members</div>
                  </div>
                </div>
              </div>

              <div className="hero-actions">
                <a href="#companies" className="btn btn-primary btn-lg me-3">
                  Explore Our Companies
                </a>
                <Link to="/gallery" className="btn btn-outline-light btn-lg">
                  View Gallery
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="trust-section py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-4 text-center">
              <div className="trust-badge">
                <i className="fas fa-shield-alt trust-icon"></i>
                <h4>Established & Trusted</h4>
                <p>15 years of proven excellence in business leadership</p>
              </div>
            </div>
            <div className="col-lg-4 text-center">
              <div className="trust-badge">
                <i className="fas fa-handshake trust-icon"></i>
                <h4>Partnership Focused</h4>
                <p>
                  Building long-term relationships with clients and investors
                </p>
              </div>
            </div>
            <div className="col-lg-4 text-center">
              <div className="trust-badge">
                <i className="fas fa-chart-line trust-icon"></i>
                <h4>Growth Oriented</h4>
                <p>Driving sustainable growth across all our ventures</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Companies Section */}
      <section id="companies" className="companies-section py-5">
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2 className="section-title">Our Established Companies</h2>
            <p className="section-subtitle">
              Explore our portfolio of successful companies, each with proven
              track records and outstanding achievements
            </p>
          </div>

          <div className="row">
            {companies.map((company) => (
              <div key={company.id} className="col-lg-4 col-md-6 mb-4">
                <div className="company-card">
                  <div className="company-header">
                    <div className="company-logo-container">
                      <img
                        src={company.logo_url}
                        alt={company.name}
                        className="company-logo"
                      />
                    </div>
                    <div className="company-basic-info">
                      <h5 className="company-name">{company.name}</h5>
                      {company.industry && (
                        <span className="company-industry">
                          {company.industry}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="company-body">
                    <p className="maincompany-description">
                      {company.short_description}
                    </p>

                    {/* Company Metrics */}
                    <div className="company-metrics">
                      <div className="metric-item">
                        <i className="fas fa-calendar-alt"></i>
                        <span>Est. {company.founded_year || "2020"}</span>
                      </div>
                      <div className="metric-item">
                        <i className="fas fa-users"></i>
                        <span>{company.employees_count || 10}+ Employees</span>
                      </div>
                    </div>

                    {/* Achievements */}
                    {company.major_achievements && (
                      <div className="achievements-section">
                        <h6 className="achievements-title">Key Achievements</h6>
                        <div className="achievements-list">
                          {company.major_achievements
                            .split(",")
                            .slice(0, 2)
                            .map((achievement, index) => (
                              <div key={index} className="achievement-item">
                                <i className="fas fa-trophy"></i>
                                <span>{achievement.trim()}</span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="company-footer">
                    <Link
                      to={`/company/${company.id}`}
                      className="btn btn-outline-primary w-100"
                    >
                      Explore
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="cta-section py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center">
              <h2 className="cta-title">Connect with Us</h2>
              <p className="cta-subtitle">
                Join our network of successful partnerships and experience the
                difference of working with established, trustworthy companies.
              </p>
              <div className="cta-actions">
                <Link to="/contact" className="btn btn-outline-light btn-lg">
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
